﻿// #region Imports
import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { AvatarSize, DEFAULT_AVATAR_SIZE, getAvatarCacheKey } from "marketplace/_views/avatars/avatar-size";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import Individual from "marketplace/individuals/individual";
// #endregion

// #region Store State Type
export type IndividualStoreState = {
  vendors: {
    entries?: IndividualJSON[];
  };
  byEntityId: {
    entries: Record<string, IndividualJSON[]>;
  };
  byUserId: {
    entries: Record<string, IndividualJSON>;
  };
  byId: {
    entries: Record<string, IndividualJSON>;
  };
  avatars: {
    entries: Record<string, string>;
  };
};
// #endregion

// #region Initial State
const initialState: IndividualStoreState = {
  vendors: {
    entries: undefined,
  },
  byEntityId: {
    entries: {},
  },
  byUserId: {
    entries: {},
  },
  byId: {
    entries: {},
  },
  avatars: {
    entries: {},
  },
};
// #endregion

// #region Slice
export const individualsSlice = createSlice({
  name: "individuals",
  initialState,
  // #region Simple Reducers
  reducers: {
    addIndividual(state, action: PayloadAction<Individual>) {
      const userId = action.payload.userId.value;
      const individualJSON = action.payload.toJSON();
      if (!state.byUserId.entries[userId]) {
        state.byUserId.entries[userId] = individualJSON;
      }
      if (!state.byId.entries[individualJSON.id]) {
        state.byId.entries[individualJSON.id] = individualJSON;
      }
    },
    addIndividualJSON: (state, action: PayloadAction<IndividualJSON>) => {
      const individualJSON = action.payload;
      if (!individualJSON.id || !individualJSON.userId) return;

      state.byUserId.entries[individualJSON.userId] = individualJSON;
      state.byId.entries[individualJSON.id] = individualJSON;
    },
    addAvatar: (state, action: PayloadAction<{ individualId: Guid; avatar: string }>) => {
      state.avatars.entries[action.payload.individualId.value] = action.payload.avatar;
      state.avatars.entries[getAvatarCacheKey(action.payload.individualId.value)] = action.payload.avatar;
    },
    removeAvatar: (state, action: PayloadAction<Guid>) => {
      delete state.avatars.entries[action.payload.value];
      delete state.avatars.entries[getAvatarCacheKey(action.payload.value, "small")];
      delete state.avatars.entries[getAvatarCacheKey(action.payload.value, "medium")];
      delete state.avatars.entries[getAvatarCacheKey(action.payload.value, "large")];
    },
  },
  // #endregion
  // #region Async Reducers
  extraReducers: (builder) => {
    // getIndividualVendors
    builder.addMatcher(individualsApi.endpoints.getIndividualVendors.matchFulfilled, (state, action) => {
      state.vendors.entries = action.payload;
    });

    // getIndividualById
    builder.addMatcher(individualsApi.endpoints.getIndividualById.matchFulfilled, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      if (!action.payload) {
        console.error("Individual not received by store.");
        return;
      }
      state.byId.entries[individualId] = action.payload;
    });

    // getIndividualsByIds
    builder.addMatcher(individualsApi.endpoints.getIndividualsByIds.matchFulfilled, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      if (!ids.length) return;
      for (const individual of action.payload) {
        state.byId.entries[individual.id] = individual;
      }
    });

    // getIndividualsByUserIds
    builder.addMatcher(individualsApi.endpoints.getIndividualsByUserIds.matchFulfilled, (state, action) => {
      if (!action.meta.arg.originalArgs) return;
      const individualsJSON: IndividualJSON[] = action.payload;

      for (const individualJSON of individualsJSON) {
        if (!individualJSON.id) {
          throw new Error("Individual data did not include id.");
        }
        state.byUserId.entries[individualJSON.userId] = individualJSON;
        state.byId.entries[individualJSON.id] = individualJSON;
      }
    });

    // getIndividualAvatar
    builder.addMatcher(individualsApi.endpoints.getIndividualAvatar.matchFulfilled, (state, action) => {
      const { individualId, size = DEFAULT_AVATAR_SIZE } = action.meta.arg.originalArgs;
      if (!individualId) return;
      state.avatars.entries[getAvatarCacheKey(individualId, size)] = action.payload ?? "";
      if(size === DEFAULT_AVATAR_SIZE) state.avatars.entries[individualId] = action.payload ?? "";
    });

    // updateIndividual
    builder.addMatcher(individualsApi.endpoints.updateIndividual.matchFulfilled, (state, action) => {
      const individualId = action.payload.id;
      state.byId.entries[individualId] = action.payload;
      state.vendors.entries = undefined;
    });

    // createIndividualAvatar
    builder.addMatcher(individualsApi.endpoints.createIndividualAvatar.matchFulfilled, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      delete state.avatars.entries[individualId];
      delete state.avatars.entries[getAvatarCacheKey(individualId, "small")];
      delete state.avatars.entries[getAvatarCacheKey(individualId, "medium")];
      delete state.avatars.entries[getAvatarCacheKey(individualId, "large")];
    });

    // getIndividualsByEntityId
    builder.addMatcher(individualsApi.endpoints.getIndividualsByEntityId.matchFulfilled, (state, action) => {
      const legalEntityId = action.meta.arg.originalArgs.legalEntityId;
      state.byEntityId.entries[legalEntityId] = action.payload;
      for (const individual of action.payload) {
        if (individual.userId) state.byUserId.entries[individual.userId] = individual;
        if (individual.id) state.byId.entries[individual.id] = individual;
      }
    });
  },
  // #endregion
});
// #endregion

// #region Exports

export const { addIndividual, addIndividualJSON } = individualsSlice.actions;

// #region Individual By Id

// getIndividualById ----------------------------------------------------------
const selectIndividualById = createSelector(
  [(state: RootState) => state.individuals.byId.entries, (_: RootState, id?: Guid) => id?.value],
  (entries, individualIdValue) => {
    if (!individualIdValue) return;
    const individualJson = entries[individualIdValue];
    if (!individualJson) return undefined;
    return Individual.fromJSON(individualJson);
  },
);
export const getIndividualById = (id?: Guid) => (state: RootState) => selectIndividualById(state, id);
// ----------------------------------------------------------------------------

// #endregion

// #region Individuals By Ids

// getIndividualsByIds --------------------------------------------------------
const selectIndividualsByIds = createSelector(
  [(state: RootState) => state.individuals.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids
      .map((id) => {
        const individualJson = entries[id.value];
        if (!individualJson) return undefined;
        return Individual.fromJSON(individualJson);
      })
      .filter((individual): individual is Individual => !!individual);
  },
);
export const getIndividualsByIds = (ids: Guid[]) => (state: RootState) => selectIndividualsByIds(state, ids);
// ----------------------------------------------------------------------------

// #endregion

// #region Individuals By UserId

// getIndividualByUserId ------------------------------------------------------
const noIndividual = () => undefined;
const individualByUserIdSelectors = new Map<string, (state: RootState) => Individual | undefined>();

/**
 * The person behind a user id — memoized PER user id, and cached so one id always yields the same
 * selector function.
 *
 * A single shared `createSelector` holds one result, so two components asking about two different
 * people evict each other and every read then rebuilds an `Individual`. `useSelector` reads through
 * `useSyncExternalStore`, which requires a stable snapshot: a freshly constructed object on each read
 * is a changed snapshot, and React re-renders until it gives up with "Maximum update depth exceeded".
 * Any surface naming more than one person at once starts it — the Comments/Changes stream names an
 * author per comment and per change — so this cache is what keeps the page alive, not an optimization.
 */
export const getIndividualByUserId = (userId?: Guid): ((state: RootState) => Individual | undefined) => {
  const userIdValue = userId?.value;
  if (!userIdValue) return noIndividual;

  const cachedSelector = individualByUserIdSelectors.get(userIdValue);
  if (cachedSelector) return cachedSelector;

  const selector = createSelector(
    [(state: RootState) => state.individuals.byUserId.entries[userIdValue]],
    (individualJson) => (individualJson ? Individual.fromJSON(individualJson) ?? undefined : undefined),
  );
  individualByUserIdSelectors.set(userIdValue, selector);
  return selector;
};
// ----------------------------------------------------------------------------

// #endregion

// #region Individual By UserIds

// getIndividualsByUserIds ----------------------------------------------------
const selectIndividualsByUserIds = createSelector(
  [(state: RootState) => state.individuals.byUserId.entries, (_: RootState, userIds: Guid[] | Set<string>) => userIds],
  (entries, userIds) => {
    const idsArray = Array.isArray(userIds) ? userIds : Array.from(userIds).map((id) => new Guid(id));
    return idsArray
      .map((userId) => {
        const individualJson = entries[userId.value];
        if (!individualJson) return undefined;
        return Individual.fromJSON(individualJson);
      })
      .filter((individual): individual is Individual => !!individual);
  },
);
export const getIndividualsByUserIds = (userIds: Guid[] | Set<string>) => (state: RootState) =>
  selectIndividualsByUserIds(state, userIds);
// ----------------------------------------------------------------------------

// #endregion

// #region Individuals By Entity

// getIndividualsByEntity -----------------------------------------------------
export const selectIndividualsByEntity = createSelector(
  [(state: RootState) => state.individuals.byEntityId.entries, (_: RootState, entityId?: Guid) => entityId?.value],
  (entries, entityId) => {
    if (!entityId) return;
    const individualJsonArray = entries[entityId];
    if (!individualJsonArray) return;
    return individualJsonArray
      .map((individualJson) => Individual.fromJSON(individualJson))
      .filter((individual): individual is Individual => !!individual);
  },
);
export const getIndividualsByEntity = (entityId?: Guid) => (state: RootState) =>
  selectIndividualsByEntity(state, entityId);
// #endregion -----------------------------------------------------------------

// #endregion

// #region Individual Vendors

// getIndividualVendors ----------------------------------------------------------
const selectIndividualVendors = createSelector([(state: RootState) => state.individuals.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Individual.fromJSON(json)).filter((c): c is Individual => !!c),
);
export const getIndividualVendors = () => (state: RootState) => selectIndividualVendors(state);
// ----------------------------------------------------------------------------

// #endregion

// #region Individual Avatar
export const getIndividualAvatar = (individualId: Guid | null, size: AvatarSize = DEFAULT_AVATAR_SIZE) => (state: RootState) =>
  individualId
    ? state.individuals.avatars.entries[getAvatarCacheKey(individualId.value, size)] ??
      (size === DEFAULT_AVATAR_SIZE ? state.individuals.avatars.entries[individualId.value] : undefined)
    : undefined;
// #endregion
